import React, { Component } from 'react';
import { connect } from "react-redux";
import Axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import i18n from '../../../../i18next';
import { Col } from 'reactstrap';

import {
    SERVER_URL,
    FRONT_END_URL,
    STRIPE_ANDROID_REQUIRE_CHROME_FOR_CHECKOUT,
} from '../../../../utils/Constants';
import {
    resetStripeBrowserHandoffFlag,
    buildStripeCheckoutHppRedirectSuccessUrl,
    buildStripeCheckoutHppRedirectCancelUrl,
} from '../../../../utils/stripeBrowserDeepLink';
import { openStripeCheckoutInChromeAndroid } from '../../../../utils/openHttpsInChromeAndroid';
import { refreshStatusBarAfterInAppBrowser } from '../../../../utils/initStatusBar';
import { transformProductPrice, getAllUrlParams, getOrderSource } from '../../../../utils/CommonUtils';
import { calculateProductPrice } from '../../../../utils/ProductWizardUtils';
import {
    placeOrder,
    processingPayment,
    errorProcessingPayment,
    validateCardInput,
    saveStripeHostedPaymentData,
    clearStripeHostedPaymentData
} from "../../../../actions/index";

/** iOS InAppBrowser.close() sometimes returns a Promise that never resolves; do not block placeOrder on it. */
function closeInAppBrowserWithTimeout(browserRef, timeoutMs) {
    const closeRet = browserRef.close();
    if (!closeRet || typeof closeRet.then !== 'function') {
        return Promise.resolve({ outcome: 'sync-or-void' });
    }
    return Promise.race([
        closeRet.then(() => ({ outcome: 'resolved' })).catch((e) => ({ outcome: 'rejected', message: e && e.message })),
        new Promise((resolve) => setTimeout(() => resolve({ outcome: 'timeout' }), timeoutMs)),
    ]);
}

/** Debug InAppBrowser handoff (Capacitor/Cordova). Filter Xcode / Safari console: StripeHostedCheckout */
function logStripeHostedIab(message, payload) {
    if (typeof window === 'undefined') return;
    const inHostedFlow =
        Capacitor.isNativePlatform() ||
        !!(window.cordova && window.cordova.InAppBrowser);
    if (!inHostedFlow) return;
    if (payload !== undefined) {
        console.log(`[StripeHostedCheckout] ${message}`, payload);
    } else {
        console.log(`[StripeHostedCheckout] ${message}`);
    }
}

class StripeHostedCheckout extends Component {
    constructor(props) {
        super(props);

        this.handleSubmit = this.handleSubmit.bind(this);
        this.stripeHostedRedirect = this.stripeHostedRedirect.bind(this);
        this.stripeHostedPaymentRequest = this.stripeHostedPaymentRequest.bind(this);
        this.stripeCapacitorHostedRedirect = this.stripeCapacitorHostedRedirect.bind(this);
        this.completeOrder = this.completeOrder.bind(this);
        /** Prevents double handoff when iOS fires multiple navigation callbacks for the same success URL */
        this._stripeHostedSuccessHandoffDone = false;
    }

    componentDidMount = () => {
        this.props.validateCardInput(true);
    }

    componentDidUpdate = (prevProps) => {
        if (prevProps.payment.isCardValid !== this.props.payment.isCardValid) {
            this.props.validateCardInput(true);
        }
    }

    componentWillUnmount = () => {
        this.props.validateCardInput(false);
    }

    getStripeAndroidReturnAppId = async () => {
        try {
            const appInfo = await App.getInfo();
            if (appInfo && appInfo.id) {
                return appInfo.id;
            }
        } catch (e) {
            logStripeHostedIab('App.getInfo failed; falling back to legacy Stripe return URL');
        }

        return null;
    }


    stripeCapacitorHostedRedirect = async (redirectUrl) => {
        if (!redirectUrl || !Capacitor.isNativePlatform()) {
            return;
        }

        if (Capacitor.getPlatform() === 'android') {
            this._stripeHostedSuccessHandoffDone = false;
            resetStripeBrowserHandoffFlag();

            const checkoutLogHost = (() => {
                try {
                    return new URL(redirectUrl).host;
                } catch (e) {
                    return '(parse error)';
                }
            })();

            if (STRIPE_ANDROID_REQUIRE_CHROME_FOR_CHECKOUT) {
                logStripeHostedIab('Android: OpenInChromePlugin (Chrome package — STRIPE_ANDROID_REQUIRE_CHROME_FOR_CHECKOUT)', {
                    initialCheckoutUrlHost: checkoutLogHost,
                });
                const opened = await openStripeCheckoutInChromeAndroid(redirectUrl, {
                    toolbarColor: '#1a1a1a',
                });
                if (!opened) {
                    const providerId = this.props.paymentProvider.providerType
                        ? this.props.paymentProvider.providerType.id
                        : 1;
                    this.props.errorProcessingPayment(
                        {},
                        'Google Chrome is required for payment. Please install Chrome from the Play Store, then try again.',
                        providerId
                    );
                    this.props.clearStripeHostedPaymentData();
                }
            } else {
                /* Same as 8170ee5709c1483ed01b1320a2ff193c4069617e: Browser.open + custom scheme + stripeBrowserDeepLink */
                logStripeHostedIab('Browser.open (Chrome Custom Tabs)', {
                    initialCheckoutUrlHost: checkoutLogHost,
                });
                await Browser.open({
                    url: redirectUrl,
                    presentationStyle: 'fullscreen',
                    toolbarColor: '#1a1a1a',
                });
            }
            return;
        }

        // iOS: embedded InAppBrowser (https success/cancel URLs → payment-redirect.html)
        if (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.InAppBrowser) {
            const InAppBrowser = window.Capacitor.Plugins.InAppBrowser;

            this._stripeHostedSuccessHandoffDone = false;

            logStripeHostedIab('registering IAB listeners (iOS)', {
                initialCheckoutUrlHost: (() => {
                    try {
                        return new URL(redirectUrl).host;
                    } catch (e) {
                        return '(parse error)';
                    }
                })(),
                frontEndUrl: FRONT_END_URL,
            });

            await InAppBrowser.addListener('browserPageNavigationCompleted', (event) => {
                const url = event && event.url;
                logStripeHostedIab(
                    `browserPageNavigationCompleted url=${url ? JSON.stringify(url) : '(none)'} successParam=${url ? url.indexOf('success=') !== -1 : false}`
                );
                this.completeOrder(event, InAppBrowser);
            });

            await InAppBrowser.addListener('browserPageLoaded', () => {
                logStripeHostedIab('browserPageLoaded (plugin does not pass URL here)');
            });

            await InAppBrowser.addListener('browserClosed', () => {
                logStripeHostedIab('browserClosed');
                void refreshStatusBarAfterInAppBrowser();
                this.completeOrder(null, null);
            });

            const options = {
                showToolbar: false,
                showURL: false,
                clearCache: false,
                clearSessionCache: false,
                mediaPlaybackRequiresUserAction: false,
                closeButtonText: 'Close',
                toolbarPosition: 0,
                showNavigationButtons: false,
                leftToRight: false,
                customWebViewUserAgent: null,
                android: {
                    allowZoom: false,
                    hardwareBack: true,
                    pauseMedia: true,
                },
                iOS: {
                    allowOverScroll: true,
                    enableViewportScale: false,
                    allowInLineMediaPlayback: false,
                    surpressIncrementalRendering: false,
                    viewStyle: 2,
                    animationEffect: 2,
                    allowsBackForwardNavigationGestures: true,
                },
            };

            logStripeHostedIab('openInWebView (iOS)');
            await InAppBrowser.openInWebView({
                url: redirectUrl,
                options: options,
            });
        }
    }

    stripeHostedRedirect = (redirectUrl) => {
        if (redirectUrl) {
            if (Capacitor.isNativePlatform()) { // Capacitor app
                this.stripeCapacitorHostedRedirect(redirectUrl);
            } else if (window.cordova && window.cordova.InAppBrowser) { // Cordova app
                this._stripeHostedSuccessHandoffDone = false;
                logStripeHostedIab('Cordova InAppBrowser open (loadstart)');
                let browserRef = window.cordova.InAppBrowser.open(redirectUrl, '_blank', 'location=no,toolbar=no');
                browserRef.addEventListener('loadstart', (event) => {
                    logStripeHostedIab('Cordova loadstart', {
                        url: event && event.url,
                        successParamPresent: !!(event && event.url && event.url.indexOf('success=') !== -1),
                    });
                    this.completeOrder(event, browserRef);
                });
            }
        }
    }

    async stripeHostedPaymentRequest() {
        const shoppingCartTotalWithGiftCard = this.props.giftCards.giftCards ? this.props.giftCards.giftCards.total : null;
        const { customer, selectedRestaurant, shoppingCart, giftCards, promotionPartners, estimateOrderTime } = this.props;
        const { orderProducts, appliedPromotions, vat } = this.props.shoppingCart;
        const orderSource = getOrderSource(this.props.pwaAppRunning);

        const externalDiscountAmount = promotionPartners.promoPartnerDiscountTotal || 0;
        const giftCardDiscount = giftCards.giftCards ? transformProductPrice(giftCards.giftCards.totalDiscount) : 0;

        let promotionsDiscount = 0;
        if (appliedPromotions && appliedPromotions.length > 0) {
            appliedPromotions.forEach(promotion => {
                promotionsDiscount += promotion.totalDiscount || 0;
            });
        }

        let totalTaxVat = 0;
        if (!estimateOrderTime.taxIncludeInPrice && vat.taxes && vat.taxes.length > 0) {
            vat.taxes.forEach(tax => {
                totalTaxVat += tax.total || 0;
            });
        }

        const orderedProductsRequest = orderProducts.map(product => ({
            name: product.name,
            quantity: Number(product.quantity),
            price: transformProductPrice(calculateProductPrice(selectedRestaurant.allowToppingSubstitution, Number(product.price), 1, product))
        }));
        const isAndroid = Capacitor.getPlatform() === 'android';
        const androidSuccessUrl = isAndroid ? buildStripeCheckoutHppRedirectSuccessUrl(null) : null;
        const androidCancelUrl = isAndroid ? buildStripeCheckoutHppRedirectCancelUrl(null) : null;

        const paymentIntentRequest = {
            customerId: customer.id,
            restaurantId: selectedRestaurant.restaurantId,
            amount: shoppingCartTotalWithGiftCard ? shoppingCartTotalWithGiftCard.toFixed(2) : shoppingCart.total,
            amountWithoutServiceFee: shoppingCart.subtotal,
            serviceFee: shoppingCart.serviceFee,
            currency: selectedRestaurant.currency,
            app: true,
            orderSource: orderSource,
            stripeAuthorizeRequest: {
                paymentMethod: null,
                newCard: false,
//                successUrl: `${FRONT_END_URL}/?success=true#/checkout/`,
//                cancelUrl: `${FRONT_END_URL}/?success=false#/checkout/`
                successUrl:
                    isAndroid
                        ? androidSuccessUrl
                        : `${FRONT_END_URL}/payment-redirect.html?success=true`,
                cancelUrl:
                    isAndroid
                        ? androidCancelUrl
                        : `${FRONT_END_URL}/payment-redirect.html?success=false`,
                orderDetails: {
                    deliveryCharge: shoppingCart.deliveryFee || 0,
                    freeDelivery: shoppingCart.freeDelivery || false,
                    giftCardDiscount: giftCardDiscount,
                    orderDeposit: shoppingCart.deposits || 0,
                    priceBeforeDiscount: shoppingCart.subtotal,
                    tip: shoppingCart.tip || 0,
                    totalTax: totalTaxVat,
                    serviceFee: shoppingCart.serviceFee || 0,
                    promotionsDiscount: promotionsDiscount,
                    externalDiscountAmount: externalDiscountAmount,
                    products: orderedProductsRequest
                }
            }
        };

        const header = { headers: { 'Authorization': `TOKEN${customer.token}` } };

        await Axios.post(`${SERVER_URL}/payment/authorize`, paymentIntentRequest, header)
            .then(response => {
                if (response.data && response.data.stripeHostedPageResponse) {
                    const redirectUrl = response.data.stripeHostedPageResponse.redirectUrl;
                    const sessionId = response.data.stripeHostedPageResponse.sessionId;

                    if (redirectUrl && sessionId) {
                        logStripeHostedIab('authorize OK, opening hosted checkout', {
                            successUrlSentToBackend:
                                isAndroid
                                    ? androidSuccessUrl
                                    : `${FRONT_END_URL}/payment-redirect.html?success=true`,
                            sessionIdSuffix: sessionId.slice(-8),
                        });
                        this.props.saveStripeHostedPaymentData(sessionId);
                        this.stripeHostedRedirect(redirectUrl);
                    }
                }
            })
            .catch(error => {
                this.props.errorProcessingPayment(error, 'Authorize Failure', this.props.paymentProvider.providerType.id);
                this.props.sendEmailReport(selectedRestaurant.restaurantId, error, 'Stripe hosted payment authorize error');
            });
    }

    completeOrder = async (event, browserRef) => {
        const stripeSessionId = this.props.paymentProvider.stripeHostedPaymentData;

        if (event && browserRef) {
            if (!event.url) {
                logStripeHostedIab('completeOrder: event has no url (may fall through to cancel path if session exists)', {
                    hasStripeSessionInState: !!stripeSessionId,
                });
            }
        }

        if (event && event.url && browserRef) {
            // Android: return is handled via custom scheme + stripeBrowserDeepLink.js (Browser / Custom Tabs).
            if (Capacitor.getPlatform() === 'android') {
                return;
            }
            if ((event.url).indexOf('success=') !== -1) {
                if (this._stripeHostedSuccessHandoffDone) {
                    logStripeHostedIab('completeOrder: duplicate success URL ignored (handoff already ran)');
                    return;
                }
                this._stripeHostedSuccessHandoffDone = true;

                logStripeHostedIab('completeOrder: success= matched, closing IAB and finishing', {
                    parsedSuccess: getAllUrlParams(event.url).success,
                    stripeSessionIdPresent: !!stripeSessionId,
                });

                if (Capacitor.isNativePlatform()) {
                    browserRef.removeAllListeners();
                } else {
                    browserRef.removeEventListener('loadstart', this.completeOrder);
                }

                const closeOutcome = await closeInAppBrowserWithTimeout(browserRef, 2500);
                logStripeHostedIab('completeOrder: InAppBrowser.close() outcome', closeOutcome);
                if (Capacitor.isNativePlatform()) {
                    void refreshStatusBarAfterInAppBrowser();
                }

                let allUrlParams = getAllUrlParams(event.url);
                const isSuccess = allUrlParams.success;

                if (isSuccess === 'true') {
                    logStripeHostedIab('completeOrder: dispatching placeOrder with hosted session');
                    this.props.placeOrder(false, stripeSessionId);
                    this.props.clearStripeHostedPaymentData();
                    logStripeHostedIab('completeOrder: placeOrder + clearStripeHostedPaymentData dispatched');
                } else if (isSuccess === 'false') {
                    this.props.errorProcessingPayment({}, 'Something went wrong.', this.props.paymentProvider.providerType.id);
                    this.props.clearStripeHostedPaymentData();
                }
            } else {
                logStripeHostedIab('completeOrder: url loaded but no success= query yet', {
                    urlSample: event.url.length > 120 ? `${event.url.slice(0, 120)}…` : event.url,
                });
            }
        } else if (this.props.paymentProvider.stripeHostedPaymentData) {
            if (Capacitor.getPlatform() === 'android') {
                return;
            }
            logStripeHostedIab('completeOrder: treating as user cancel / close (no success url branch)');
            this.props.errorProcessingPayment({}, i18n.t(`screens:shoppingCartActions.cancelledByUser`), this.props.paymentProvider.providerType.id);
            this.props.clearStripeHostedPaymentData();
        }
    }

    handleSubmit = (e) => {
        e.preventDefault();
        this.props.processingPayment();
        this.stripeHostedPaymentRequest();
    }

    render = () => {
        const { primaryFontColor } = this.props.customerThemes.selectedTheme;
        const { cardsInfoMessage } = this.props.styles;
        const isMobileView = window.innerWidth <= 865;

        return (
            <Col>
                <form onSubmit={this.handleSubmit} id="payment-form" hidden={true} /> 
                {this.props.payment.selectedPaymentType && this.props.payment.selectedPaymentType.id === 1 &&
                <p
                    style={!this.props.pwaAppRunning && isMobileView ? {...cardsInfoMessage, color: primaryFontColor, position: 'none', left: 'unset'} :
                    {...cardsInfoMessage, color: primaryFontColor}}>*{i18n.t('common:alerts.paymentProvidersWarning')}
                </p>
                }
            </Col>
        );
    }
}

const mapStateToProps = state => {
    return {
        customer: state.customer,
        payment: state.payment,
        selectedRestaurant: state.selectedRestaurant,
        shoppingCart: state.shoppingCart,
        customerThemes: state.customerThemes,
        pwaAppRunning: state.pwaAppRunning,
        giftCards: state.giftCards,
        promotionPartners: state.promotionPartners,
        paymentProvider: state.paymentProvider
    };
};

export default connect(mapStateToProps, { 
    placeOrder, 
    processingPayment, 
    errorProcessingPayment, 
    validateCardInput,
    saveStripeHostedPaymentData,
    clearStripeHostedPaymentData
})(StripeHostedCheckout);